import React, { useEffect } from 'react';
import styles from '../../styles/renewal/Forms.module.css';
import page from '../../styles/renewal/OnePage.module.css';
import useForm, { buildValidate, validators } from './useForm';
import submitInquiry from './submitInquiry';
import { PARTNER_KINDS } from './data';
import { TextField, SelectField, TextareaField, AgreeField, StatusBanner } from './Fields';

const INITIAL = {
  company: '',
  manager: '',
  phone: '',
  email: '',
  kind: PARTNER_KINDS[0],
  proposal: '',
  agree: false,
};

const validate = buildValidate({
  company: [validators.required('상호 또는 법인명을 입력해 주세요.')],
  manager: [validators.required('담당자명을 입력해 주세요.')],
  phone: [
    validators.required('연락처를 입력해 주세요.'),
    validators.phone('연락처 형식을 확인해 주세요. (예: 010-1234-5678)'),
  ],
  email: [
    validators.required('이메일을 입력해 주세요.'),
    validators.email('이메일 형식을 확인해 주세요.'),
  ],
  proposal: [validators.required('제휴 내용을 간단히 적어주세요.')],
  agree: [validators.checked('개인정보 수집 및 이용에 동의해 주세요.')],
});

const PartnerForm = ({ open, onClose }) => {
  const { values, status, submitting, errorOf, handleChange, handleBlur, handleSubmit } = useForm({
    initialValues: INITIAL,
    validate,
    onSubmit: (v) => submitInquiry('partner', v),
  });

  // Esc 로 닫기 + 열려 있는 동안 배경 스크롤 잠금
  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const bind = (name) => ({
    name,
    value: values[name],
    onChange: handleChange,
    onBlur: handleBlur,
    error: errorOf(name),
  });

  return (
    <>
      <button type="button" className={styles.scrim} aria-label="닫기" onClick={onClose} />

      <div
        className={styles.drawer}
        role="dialog"
        aria-modal="true"
        aria-labelledby="partner-form-title"
      >
        <div className={styles.drawerHead}>
          <div className={styles.drawerTitle} id="partner-form-title">
            제휴 신청
          </div>
          <button type="button" className={styles.close} onClick={onClose} title="닫기">
            <span aria-hidden="true">✕</span>
            <span className={styles.srOnly}>닫기</span>
          </button>
        </div>

        <p className={styles.drawerLead}>
          접수 후 담당자가 확인하여 영업일 기준 2일 내에 연락드립니다.
        </p>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={`${styles.row} ${styles.rowWide}`}>
            <TextField
              id="partner-company"
              label="사업장명"
              required
              placeholder="상호 또는 법인명을 입력해 주세요"
              autoComplete="organization"
              autoFocus
              {...bind('company')}
            />
            <TextField
              id="partner-manager"
              label="담당자명"
              required
              placeholder="담당자명을 입력해 주세요"
              autoComplete="name"
              {...bind('manager')}
            />
          </div>

          <div className={`${styles.row} ${styles.rowWide}`}>
            <TextField
              id="partner-phone"
              label="연락처"
              required
              type="tel"
              inputMode="tel"
              placeholder="010-0000-0000"
              autoComplete="tel"
              {...bind('phone')}
            />
            <TextField
              id="partner-email"
              label="이메일"
              required
              type="email"
              placeholder="name@company.co.kr"
              autoComplete="email"
              {...bind('email')}
            />
          </div>

          <SelectField
            id="partner-kind"
            label="제휴 유형"
            options={PARTNER_KINDS}
            {...bind('kind')}
          />

          <TextareaField
            id="partner-proposal"
            label="제휴 제안"
            required
            placeholder="제휴 내용과 기대하는 협업방식을 간단히 적어주세요."
            {...bind('proposal')}
          />

          <div className={styles.agreeRow}>
            <AgreeField
              id="partner-agree"
              name="agree"
              checked={values.agree}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errorOf('agree')}
            >
              개인정보 수집 및 이용에 동의합니다.
            </AgreeField>
          </div>

          <StatusBanner
            status={status}
            successText="제휴 신청이 접수되었습니다. 영업일 기준 2일 내에 담당자가 연락드립니다."
            errorText="접수 중 문제가 발생했습니다. 잠시 후 다시 시도하시거나 1544-3230으로 연락해 주세요."
          />

          <button
            type="submit"
            className={`${page.btn} ${page.btnDark} ${page.btnBlock}`}
            disabled={submitting}
          >
            {submitting ? '접수 중…' : '제휴 신청하기 →'}
          </button>
        </form>
      </div>
    </>
  );
};

export default PartnerForm;
